import React, { useEffect, useState } from "react";
import { Wifi, WifiOff, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import queue from "../lib/uploadQueue";

/**
 * SyncPill — sparse, operational sync state indicator for the field top bar.
 *
 * States:
 *   ONLINE   (green dot)
 *   OFFLINE  (red wifi-off)
 *   SYNCING  (spinner + count)
 *   N QUEUED (amber when items are pending)
 *   FAILED   (red triangle)
 */
export default function SyncPill() {
  const [s, setS] = useState(queue.snapshot());
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine !== false : true);

  useEffect(() => {
    const unsub = queue.subscribe(setS);
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      unsub();
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  if (!online) {
    return (
      <button
        type="button"
        onClick={() => queue.flush()}
        data-testid="sync-pill"
        data-state="offline"
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-extrabold uppercase tracking-[0.12em]"
        style={{ background: "rgba(230,20,50,0.10)", color: "var(--bad)", border: "1px solid rgba(230,20,50,0.30)" }}
        title="No connection — actions will queue and sync when back online"
      >
        <WifiOff size={11} />
        OFFLINE{s.items.length > 0 ? ` · ${s.items.length}` : ""}
      </button>
    );
  }

  if (s.syncing) {
    return (
      <span data-testid="sync-pill" data-state="syncing"
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-extrabold uppercase tracking-[0.12em]"
            style={{ background: "rgba(70,40,140,0.12)", color: "var(--accent)", border: "1px solid rgba(70,40,140,0.30)" }}>
        <Loader2 size={11} className="animate-spin" />
        SYNCING{s.items.length > 0 ? ` · ${s.items.length}` : ""}
      </span>
    );
  }

  if (s.failed > 0) {
    return (
      <button
        type="button"
        onClick={() => queue.flush()}
        data-testid="sync-pill"
        data-state="failed"
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-extrabold uppercase tracking-[0.12em]"
        style={{ background: "rgba(230,20,50,0.12)", color: "var(--bad)", border: "1px solid rgba(230,20,50,0.30)" }}
        title="Retry failed uploads"
      >
        <AlertTriangle size={11} />
        {s.failed} FAILED
      </button>
    );
  }

  if (s.pending > 0) {
    return (
      <button
        type="button"
        onClick={() => queue.flush()}
        data-testid="sync-pill"
        data-state="queued"
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-extrabold uppercase tracking-[0.12em]"
        style={{ background: "rgba(216,144,52,0.12)", color: "var(--warn)", border: "1px solid rgba(216,144,52,0.30)" }}
        title="Tap to sync now"
      >
        <Loader2 size={11} />
        {s.pending} QUEUED
      </button>
    );
  }

  // Quiet steady-state — small dot, hidden on very small viewports
  return (
    <span
      data-testid="sync-pill"
      data-state="online"
      className="hidden sm:inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-extrabold uppercase tracking-[0.12em]"
      style={{ background: "rgba(27,154,92,0.08)", color: "var(--good)", border: "1px solid rgba(27,154,92,0.25)" }}
    >
      <CheckCircle2 size={11} />
      SYNCED
    </span>
  );
}
